/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Distance formatting helpers — display in imperial (miles/feet) for US users.
// Callers that interact with external APIs (Google Places "radius" param) should
// keep working in meters; only convert at the display boundary.

const KM_PER_MILE = 1.60934;
const METERS_PER_FOOT = 0.3048;
const EARTH_RADIUS_MILES = 3958.7613;

// Friends beyond this many miles are shown as "Too far" rather than an
// exact distance. Picked to match the Build 85 Friends spec.
export const TOO_FAR_THRESHOLD_MILES = 100;

export function kmToMiles(km: number): number {
  return km / KM_PER_MILE;
}

export function milesToKm(miles: number): number {
  return miles * KM_PER_MILE;
}

// CANONICAL DISTANCE FORMAT — SINGLE SOURCE OF TRUTH for distance display.
//   >= 0.2 mi  → "{X.X} mi"  (miles, exactly 1 decimal)
//   <  0.2 mi  → "{N} ft"    (feet, rounded to nearest 10)
// (1 km = 0.621371 mi; 1 mi = 5280 ft.)
// NOTE: api/chat.js mirrors this server-side (separate bundle — it cannot import
// this file). If you change the threshold/format here, change it there too.
// The Friends list uses the SEPARATE formatDistance(miles) below, which keeps
// its own "Too far" privacy cap — do NOT route it through this one.
export function formatDistanceMi(km: number): string {
  const miles = kmToMiles(km);
  if (miles < 0.2) {
    const feet = Math.round((km * 1000) / METERS_PER_FOOT / 10) * 10;
    return `${feet} ft`;
  }
  return `${miles.toFixed(1)} mi`;
}

/**
 * Canonical distance with a trailing " away" — used on the map's safe-haven
 * readout where the suffix reads naturally ("0.5 mi away"). Same numeric format
 * as formatDistanceMi; suffix only.
 */
export function formatDistanceMiAway(km: number): string {
  return `${formatDistanceMi(km)} away`;
}

/**
 * Great-circle distance in miles between two lat/lng points using the
 * Haversine formula. Used by the Friends list to compute and sort by
 * distance; keep this in miles to avoid round-tripping through km at
 * every render.
 */
export function haversineDistanceMiles(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const toRad = (d: number) => d * (Math.PI / 180);
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_MILES * c;
}

export function isTooFar(miles: number): boolean {
  return miles > TOO_FAR_THRESHOLD_MILES;
}

/**
 * Format a friend-list distance. Returns "Too far" above the 100mi cap so
 * the UI grays the row out without revealing a precise location.
 *
 * Under 1 mi: one decimal (e.g. "0.4 miles").
 * 1 mi exactly (rounded): "1 mile" (singular).
 * 1–10 mi: one decimal (e.g. "3.2 miles").
 * 10–100 mi: integer (e.g. "47 miles").
 * Above 100 mi: "Too far".
 */
export function formatDistance(miles: number): string {
  if (isTooFar(miles)) return "Too far";
  if (miles < 1) return `${miles.toFixed(1)} miles`;
  if (miles < 10) {
    const rounded = Math.round(miles * 10) / 10;
    if (rounded === 1) return "1 mile";
    return `${rounded.toFixed(1)} miles`;
  }
  const rounded = Math.round(miles);
  if (rounded === 1) return "1 mile";
  return `${rounded} miles`;
}
